import Button from "@/components/Button";
import PageMessage from "@/components/PageMessage";
import { AppState, Auth0Provider, useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import OktaAuth, { toRelativeUrl } from "@okta/okta-auth-js";
import { Security, useOktaAuth, LoginCallback } from "@okta/okta-react";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { clearAssumedUserStorage } from "@/providers/AssumeUserProvider/storage";

type AuthProviderType = "auth0" | "okta" | "token";

type IAuthenticationProvider = ({ children }: { children: ReactNode }) => JSX.Element;

// eslint-disable-next-line react-refresh/only-export-components
export const authProviderType: AuthProviderType = import.meta.env.VITE_AUTH_PROVIDER || "auth0";

const AuthenticationProvider: IAuthenticationProvider = (() => {
  if (authProviderType === "okta") {
    function OktaAuthenticationProvider({ children }: { children: ReactNode }) {
      const oktaAuth = new OktaAuth({
        issuer: import.meta.env.VITE_OKTA_ISSUER,
        clientId: import.meta.env.VITE_OKTA_CLIENT_ID,
        redirectUri: window.location.origin,
        scopes: ["openid", "profile", "email", "offline_access"]
      });

      const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
        window.location.replace(toRelativeUrl(originalUri || "/", window.location.origin));
      };

      const UnauthorizedRedirect = () => {
        useEffect(() => {
          const originalUri = toRelativeUrl(window.location.href, window.location.origin);
          oktaAuth.setOriginalUri(originalUri);
          oktaAuth.signInWithRedirect();
        }, []);
        return <PageMessage title="Redirecting to Member 360 Login" />;
      };

      const ProtectedChildren = () => {
        const { authState, oktaAuth } = useOktaAuth();
        const memoizedChildren = useMemo(() => <> {children} </>, []);
        const isLoginCallbackLoading = useRef(false);

        if (oktaAuth.isLoginRedirect()) {
          isLoginCallbackLoading.current = true;
          return <LoginCallback />;
        }

        if (authState == null || (!authState.isAuthenticated && isLoginCallbackLoading.current)) return null;

        if (authState.isAuthenticated) return memoizedChildren;

        // the user isn't authenticated, so redirect to login
        return <UnauthorizedRedirect />;
      };

      return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <ProtectedChildren />
        </Security>
      );
    }

    return OktaAuthenticationProvider;
  }

  if (authProviderType === "token") {
    function TokenAuthenticationProvider({ children }: { children: ReactNode }) {
      return <>{children}</>;
    }
    return TokenAuthenticationProvider;
  }

  function Auth0AuthenticationProvider({ children }: { children: ReactNode }) {
    function ProtectedChildren() {
      const { isLoading, error, logout } = useAuth0();
      const handleLogout = async () => {
        clearAssumedUserStorage();
        await logout({ logoutParams: { returnTo: window.location.origin } });
      };

      if (isLoading) {
        return <></>;
      }
      if (error) {
        return (
          <PageMessage title="Unauthorized" message={error.message} showLoadingIndicator={false}>
            <Button onClick={handleLogout}>Logout</Button>
          </PageMessage>
        );
      }
      const AuthenticatedChildren = withAuthenticationRequired(() => <>{children}</>, {
        onRedirecting: () => <PageMessage title="Redirecting to Member 360 Login" />
      });
      return <AuthenticatedChildren />;
    }
    return (
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{ redirect_uri: window.location.origin, audience: import.meta.env.VITE_AUTH0_AUDIENCE }}
        cacheLocation="localstorage"
        useRefreshTokens
        useRefreshTokensFallback
        onRedirectCallback={(appState?: AppState) => {
          if (appState?.returnTo) {
            window.location.assign(appState.returnTo);
          }
        }}
      >
        <ProtectedChildren />
      </Auth0Provider>
    );
  }

  return Auth0AuthenticationProvider;
})();

export default AuthenticationProvider;
